/**
 * More Screen
 *
 * Hub for secondary destinations: Community, LoreNote, and Account.
 * Matches the mobile "More" tab layout, sized for desktop.
 *
 * @module components/more/MoreScreen
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import './MoreScreen.css';

const LORE_NOTE_URL_PROD = 'https://lore-note.lorehealthcare.com';
const LORE_NOTE_URL_NONPROD = 'https://lore-note-non-prod.lorehealthcare.com';

/**
 * Returns the LoreNote URL for the current environment.
 * Only an explicit prod build gets the prod instance; everything else
 * (nonprod, development, localhost) falls back to non-prod.
 */
function getLoreNoteUrl(): string {
  const env = process.env.REACT_APP_ENV || 'development';
  const isProd = env === 'prod' || env === 'production';
  return isProd ? LORE_NOTE_URL_PROD : LORE_NOTE_URL_NONPROD;
}

interface MoreItem {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onActivate: () => void;
}

export const MoreScreen: React.FC = () => {
  const navigate = useNavigate();

  const items: MoreItem[] = [
    {
      id: 'community',
      title: 'Community',
      subtitle: 'Open the full Community experience',
      icon: (
        <img
          src="/icons/tabs/community_outline.svg"
          alt=""
          className="more-item-icon-img"
        />
      ),
      onActivate: () => {
        window.location.href = '/community';
      },
    },
    {
      id: 'lorenote',
      title: 'LoreNote',
      subtitle: 'Prepare a guided note for your visit',
      icon: <DescriptionOutlinedIcon className="more-item-icon-mui" />,
      onActivate: () => {
        window.open(getLoreNoteUrl(), '_blank', 'noopener,noreferrer');
      },
    },
    {
      id: 'account',
      title: 'Account',
      subtitle: 'Profile, privacy, notifications, and help',
      icon: <PersonOutlineIcon className="more-item-icon-mui" />,
      onActivate: () => {
        navigate('/account');
      },
    },
  ];

  return (
    <div className="more-screen">
      <h1 className="more-title">More</h1>
      <div className="more-list">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className="more-item"
            onClick={item.onActivate}
          >
            <span className="more-item-icon" aria-hidden>
              {item.icon}
            </span>
            <span className="more-item-text">
              <span className="more-item-title">{item.title}</span>
              <span className="more-item-subtitle">{item.subtitle}</span>
            </span>
            <ChevronRightIcon className="more-item-chevron" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
};
